import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import * as d3 from 'd3';
import * as d3Collection from 'd3-collection';

const gradeColors = ['#4e79a7', '#76b7b2', '#edc949', '#f28e2c', '#e15759'];

const ViewToggle = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 14px;
`;

const ToggleBtn = styled.button`
  font-family: inherit;
  font-size: 15px;
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  color: ${(props) => (props.$active ? '#000' : '#999')};
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  border-bottom: 2px solid ${(props) => (props.$active ? '#000' : 'transparent')};
  transition: color 0.15s ease;

  &:hover {
    color: #000;
  }
`;

const makeGradient = (gradient) => {
  gradeColors.forEach((color, i) => {
    gradient
      .append('stop')
      .attr('offset', `${20 * i}%`)
      .attr('stop-color', color)
      .attr('stop-opacity', 1);
    gradient
      .append('stop')
      .attr('offset', `${20 * (i + 1)}%`)
      .attr('stop-color', color)
      .attr('stop-opacity', 1);
  });
};

const CourseDist = ({ data }) => {
  const courseDist = useRef(null);

  const [view, setView] = useState('letter');
  const hasAnyPnp = data.some((d) => +d.nPNPStudents > 0);
  const activeView = hasAnyPnp ? view : 'letter';

  const makeLetter = (d) => ({ ...d });
  const makePnp = (d) => {
    const p = { ...d };
    delete p.nLetterStudents;
    delete p.su;
    return p;
  };
  const makeSu = (d) => {
    const s = { ...d };
    delete s.nLetterStudents;
    delete s.nPNPStudents;
    return s;
  };

  const allData = data
    .map((d) => {
      const hasLetter = +d.nLetterStudents > 0;
      const hasPnp = +d.nPNPStudents > 0;
      const hasSu = +d.su > 0;

      // Pass / No Pass view: only offerings that actually had P/NP students.
      if (activeView === 'pnp') {
        return hasPnp ? [makePnp(d)] : [];
      }
      // Letter (default) view: one row per offering, showing its primary
      // distribution (letter ridge, or a bar for inherently P/NP-only or
      // S/U-only courses). No duplicate second visualization.
      if (hasLetter) return [makeLetter(d)];
      if (hasPnp) return [makePnp(d)];
      if (hasSu) return [makeSu(d)];
      return [];
    })
    .reduce((a, b) => [...a, ...b], [])
    .map((e) => ({ ...e, order: 0 }));

  const letterLegend = allData.some((d) => +d.nLetterStudents > 0);
  const pnpLegend = allData.some(
    (d) => d.nLetterStudents === undefined && d.nPNPStudents !== undefined,
  );
  const suLegend = allData.some(
    (d) => d.nLetterStudents === undefined && d.nPNPStudents === undefined,
  );

  useEffect(() => {
    if (!courseDist.current || !allData[0]) {
      return;
    }

    const innerHeight = 50;

    const size = {
      width: Math.min(600, window.innerWidth - 40),
      height: innerHeight * allData.length,
    };

    const margin = {
      top: 20,
      right: 20,
      bottom: 10,
      left: 90,
    };

    const container = d3
      .select(courseDist.current)
      .style('width', `${size.width}px`);

    // .style('left', '20px')
    container.selectAll('*').remove();

    const grades = ['A', 'B', 'C', 'D', 'F'];

    const legendBoxes = (vals, div) => {
      vals.forEach(({ label, col }) => {
        const con = div
          .append('div')
          .style('display', 'flex')
          .style('height', '20px');

        con.style('margin', '0 10px');

        con
          .append('div')
          .style('width', '20px')
          .style('height', '20px')
          .style('background-color', col);

        con.append('p').text(label).style('margin', '0 0 0 5px');
      });
    };
    if (letterLegend) {
      const pnpDiv = container
        .append('div')
        .style('width', `${size.width}px`)
        .style('display', 'flex')
        .style('flex-wrap', 'wrap')
        .style('align-items', 'center');
      pnpDiv.append('p').text('Letter grading options:');
      const combined = gradeColors.map((col, i) => ({ label: grades[i], col }));
      legendBoxes(combined, pnpDiv);
    }
    if (pnpLegend) {
      const pnpDiv = container
        .append('div')
        .style('width', `${size.width}px`)
        .style('display', 'flex')
        .style('flex-wrap', 'wrap')
        .style('align-items', 'center');
      pnpDiv.append('p').text('P/NP grading options:');
      legendBoxes(
        [
          { label: 'P', col: '#08519C66' },
          { label: 'NP', col: '#E15759' },
        ],
        pnpDiv,
      );
    }
    if (suLegend) {
      const pnpDiv = container
        .append('div')
        .style('width', `${size.width}px`)
        .style('display', 'flex')
        .style('flex-wrap', 'wrap')
        .style('align-items', 'center');

      pnpDiv.append('p').text('Satisfactory/Unsatisfactory grading options:');
      legendBoxes(
        [
          { label: 'S', col: '#D9694266' },
          { label: 'U', col: '#D96942' },
        ],
        pnpDiv,
      );
    }

    const hoverArea = container.append('div').style('position', 'relative');

    const x = d3
      .scalePoint()
      .domain(grades)
      .range([margin.left, size.width - margin.right])
      .padding(0.5);

    const xProp = d3
      .scaleLinear()
      .range([margin.left, size.width - margin.right]);

    const svg = hoverArea
      .append('svg')
      .attr('width', size.width)
      .attr('height', size.height + innerHeight); // for last plot and axis
    const tooltip = hoverArea
      .append('div')
      .style('height', `${size.height}px`)
      .style('display', 'none')
      .style('pointer-events', 'none')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('padding', '10px')
      .style('border-radius', '10px')
      .style('border', '1px solid #d3d3d3');
    const y = d3
      .scalePoint()
      .domain(
        allData
          .map((d) => `${d.quarter} ${d.year} ${d.instructor} ${d.order}`)
          .reverse(),
      )
      .range([margin.top, size.height - margin.bottom]);

    const z = d3.scaleLinear().domain([0, 1]).range([innerHeight, -10]);

    const courses = svg
      .selectAll('courses')
      .data(allData)
      .join('g')
      .attr(
        'transform',
        (d) => `translate(0, ${y(
          `${d.quarter} ${d.year} ${d.instructor} ${d.order}`,
        )})`,
      );

    // svg
    //   .append("g")
    //   .attr("transform", `translate(0, ${size.height + (innerHeight * 3) / 4})`)
    //   .style("font-family", "Helvetica Neue,Helvetica,Arial,sans-serif")
    //   .style("font-size", "16px")
    //   .attr("color", "#adadad")
    //   .call(d3.axisBottom(x));

    courses
      .append('text')
      .text((d) => `${d.quarter} ${d.year}`)
      .attr('alignment-baseline', 'hanging')
      .attr('x', 0)
      .attr('y', z(0.6) + 8)
      .attr('font-size', '12px');

    courses
      .append('text')
      .text((d) => d.instructor)
      .attr('alignment-baseline', 'hanging')
      .attr('x', 0)
      .attr('y', z(0.6) + 8 + 12)
      .attr('font-size', '10px');

    courses
      .append('text')
      .text((d) => `Students: ${(+d.nLetterStudents) ? d.nLetterStudents : (d.nPNPStudents !== undefined) ? d.nPNPStudents : Number(d.S) + Number(d.su)}`)
      .attr('alignment-baseline', 'hanging')
      .attr('x', 0)
      .attr('y', z(0.6) + 8 + 24)
      .attr('font-size', '10px');


    const area = d3
      .area()
      .x((d) => {
        if (d.data.grade === 'A') {
          return margin.left;
        }
        if (d.data.grade === 'F') {
          return size.width - margin.right;
        }
        return x(d.data.grade);
      })
      .y0((d) => z(d[0]))
      .y1((d) => z(d[1]))
      .curve(d3.curveBumpX);

    const opac = {
      m: 0.4,
      f: 0.7,
      p: 1,
    };
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', 'svgGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');

    makeGradient(gradient);

    courses
      .filter((d) => +d.nLetterStudents)
      .selectAll('ridges')
      .data((d) => {
        const nested = d3Collection
          .nest()
          .key((d) => d[0][0])
          .entries(Object.entries(d).slice(0, 13));

        const dat = nested.map((n) => {
          const output = n.key === 'F' ? { m: 0, p: 0 } : {};

          n.values.forEach(([key, num]) => {
            output[key.length === 2 ? key[1] : 'f'] = +num / +d.nLetterStudents;
            output[key.length === 2 ? `n${key[1]}` : 'nf'] = +num;
          });

          return { grade: n.key, ...output, n: +d.nLetterStudents };
        });
        return d3.stack().keys(['m', 'f', 'p'])(dat);
      })
      .enter()
      .append('path')
      .attr('fill-opacity', (d) => opac[d.key])
      .attr('fill', 'url(#svgGradient)')
      .attr('d', (d) => area(d.map((d) => ({ 0: 0, 1: 0, data: d.data }))))
      .on('mouseout', () => {
        tooltip.style('display', 'none');
      })
      .on('mousemove', (event, d) => {
        const [x, y] = d3.pointer(event);
        const innerWidth = size.width - margin.left - margin.right;
        const innerX = x - margin.left;

        if (innerX < 0 || innerX > innerWidth) {
          return;
        }
        const letterType = d.key;
        const letter = grades[Math.floor((innerX / innerWidth) * 5)];
        const letterData = d.find((d) => d.data.grade === letter);

        const width = 190;
        tooltip.style('display', 'block');

        const nStudents =
          letter === 'F'
            ? letterData.data.nf
            : letterData.data.nf + letterData.data.nm + letterData.data.np;
        const pctStudents =
          letter === 'F'
            ? letterData.data.f
            : letterData.data.f + letterData.data.m + letterData.data.p;

        const marginFontStyle = 'margin: 2px 0; font-size: 12pt;';

        tooltip
          .style('width', `${width}px`)
          .style('height', '145px')
          .style('left', `${Math.min(event.layerX, size.width - width)}px`)
          .style('top', `${event.layerY}px`)
          .html(
            `<h3 style="margin: 0; color: ${
              gradeColors[grades.indexOf(letter)]
            }">${letter}'s</h3><hr style='border: none; border-top: 2px solid #d3d3d3;'/>`
              + `<p style='${marginFontStyle}'>Total # ${letter}'s: ${nStudents}</p>`
              + `<p style='${marginFontStyle}'>Total % ${letter}'s: ${Math.round(
                pctStudents * 100,
              )}%</p>${
                letter !== 'F'
                  ? `<p style='${marginFontStyle} font-weight: ${
                    letterType === 'p' ? 'bold' : 'normal'
                  }'>${letter}+: ${letterData.data.np} students, ${Math.round(
                    letterData.data.p * 100,
                  )}%</p> `
                  : ''
              }<p style='${marginFontStyle} font-weight: ${
                letterType === 'f' ? 'bold' : 'normal'
              }'>${letter}: ${letterData.data.nf} students, ${Math.round(
                letterData.data.f * 100,
              )}%</p> ${
                letter !== 'F'
                  ? `<p style='${marginFontStyle} font-weight: ${
                    letterType === 'm' ? 'bold' : 'normal'
                  }'>${letter}-: ${letterData.data.nm} students, ${Math.round(
                    letterData.data.m * 100,
                  )}%</p>`
                  : ''
              }`,
          );
      })
      .transition()
      .duration(2000)
      .attr('d', area);

    const barHeight = 20;

    courses
      .filter((d) => d.nLetterStudents === undefined)
      .selectAll('bars')
      .data((d) => {
        const prop =
          d.nPNPStudents !== undefined ? +d.P / +d.nPNPStudents : +d.S / +d.su;
        const color = +d.nPNPStudents > 0 ? '#08519C' : '#D96942';
        return [
          {
            ...d,
            x: 0,
            end: prop,
            color,
          },
          {
            ...d,
            x: prop,
            end: 1,
            color,
          },
        ];
      })
      .enter()
      .append('rect')
      .attr('x', (d) => xProp(d.x))
      .attr('height', barHeight)
      .attr('y', innerHeight / 2 - barHeight / 2 + 5)
      .attr('width', (d) => xProp(d.end) - xProp(d.x))
      .attr('fill', (d, i) => (i === 0 ? d.color : '#E15759'))
      .attr('fill-opacity', (d, i) => (i === 0 ? 0.4 : 1));

    courses
      .filter((d) => d.nLetterStudents === undefined)
      .selectAll('barLabels')
      .data((d) => {
        const prop =
          d.nPNPStudents !== undefined ? +d.P / +d.nPNPStudents : +d.S / +d.su;

        return prop === 1 ? [prop] : [prop, prop];
      })
      .enter()
      .append('text')
      .text((d, i) => `${Math.round((i === 0 ? d : 1 - d) * 100)}%`)
      .attr('x', (d, i) => xProp(d) + (i === 0 ? -5 : 5))
      .attr('text-anchor', (d, i) => (i === 0 ? 'end' : 'start'))
      .attr('y', innerHeight - barHeight + 5);
  }, [data, activeView]);

  return (
    <div>
      {hasAnyPnp ? (
        <ViewToggle>
          <ToggleBtn
            type="button"
            $active={activeView === 'letter'}
            onClick={() => setView('letter')}
          >
            Letter grades
          </ToggleBtn>
          <ToggleBtn
            type="button"
            $active={activeView === 'pnp'}
            onClick={() => setView('pnp')}
          >
            Pass / No Pass
          </ToggleBtn>
        </ViewToggle>
      ) : null}
      <div ref={courseDist} />
    </div>
  );
};

export default CourseDist;
